import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { listObras } from '@/lib/obraCache';

interface PreencherRelatorioModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: { obra: string; date: Date; copyLastRDO: boolean }) => void; // Opcional, o modal navega por padrão
  defaultObra?: string;
}

export const PreencherRelatorioModal: React.FC<PreencherRelatorioModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultObra
}) => {
  const navigate = useNavigate();
  const [selectedObra, setSelectedObra] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [copyLastRDO, setCopyLastRDO] = useState(false);

  // Fonte única: obras reais do usuário (obraCache, hidratado do Supabase)
  const allObras = useMemo(() => {
    return listObras().map(obra => ({
      value: obra.id,
      label: obra.nomeObra || "Obra sem nome"
    }));
  }, [open]); // Re-compute when modal opens

  // Set default values when modal opens
  useEffect(() => {
    if (open) {
      setSelectedDate(new Date()); // Always set today as default
      if (defaultObra) {
        setSelectedObra(defaultObra);
      }
    }
  }, [open, defaultObra]);

  const handleSubmit = () => {
    if (selectedObra && selectedDate) {
      const data = {
        obra: selectedObra,
        date: selectedDate,
        copyLastRDO
      };

      // Se há um onSubmit personalizado, usa ele, senão navega diretamente
      if (onSubmit) {
        onSubmit(data);
      } else {
        // Navegação padrão para a página de preenchimento
        const params = new URLSearchParams({
          obraId: data.obra,
          date: data.date.toISOString().split('T')[0], // Format as YYYY-MM-DD
          copyLastRDO: data.copyLastRDO.toString()
        });
        
        navigate(`/rdo/preencher?${params.toString()}`);
        onOpenChange(false);
      }
      
      // Reset form
      setSelectedObra("");
      setSelectedDate(undefined);
      setCopyLastRDO(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Reset form
    setSelectedObra("");
    setSelectedDate(undefined);
    setCopyLastRDO(false);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Preencher RDO</DialogTitle>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="obra">Obra</Label>
            <Select value={selectedObra} onValueChange={setSelectedObra}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma obra" />
              </SelectTrigger>
              <SelectContent>
                {allObras.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    Nenhuma obra cadastrada. Crie uma obra primeiro.
                  </div>
                ) : (
                  allObras.map((obra) => (
                    <SelectItem key={obra.value} value={obra.value}>
                      {obra.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Data</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "dd/MM/yyyy") : "Selecionar data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date > today}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="copyLastRDO"
              checked={copyLastRDO}
              onCheckedChange={(checked) => setCopyLastRDO(checked as boolean)}
            />
            <Label htmlFor="copyLastRDO">Copiar último RDO</Label>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit}>
            Preencher
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};